import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import classNames from 'classnames';
import moment from 'moment';
import { Intent } from '@blueprintjs/core';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { LinkButton } from 'components/buttons';
import { DataDisplayRecord } from 'components/data-display';
import { DateToLocal } from 'components/dates';
import { GridLayout } from 'components/grid';
import { ShowHide } from 'components/layout';
import { Breadcrumbs } from 'components/navigation';
import { AlertUnsavedData, NotificationInline, NotificationToaster } from 'components/notifications';
import { UserAccess, UserHasAccess } from 'components/user-access';
import { Wizard, WizardSteps } from 'components/wizard';
import { useDirtyData } from 'hooks/useDirtyData';
import { useValidation } from 'hooks/useValidation';
import {
    getTopicIdsByKeys,
    getTriggerValidationRules,
    mapTriggersFromApi,
    toTriggerErrors,
    toSignatureValue,
    RaisedByDisplay,
    toTriggerRequest
} from 'pages/employee-reviews/shared';
import { PageRestricted } from 'pages/errors/page-restricted';
import { EmployeeReviewsService } from 'services';
import {
    buildAdditionalTriggersGating,
    buildCompleteReviewRequest,
    buildOutcomeGating,
    buildSignOffGating,
    emailSignOffMethodKey,
    resolveEmployeeSignOffMethodKey,
    statusBands
} from './employeeReviewCompleteHelpers';
import { AdditionalTriggersStep, IntroductionStep, OutcomeStep, SignOffStep } from './steps';
import { buildTopicComments, buildTopicRequestFields, getReviewTopics, initialTopicStates } from './topics';

const requiredAction = "EmployeeReview:Perform";
const listingRoute = "/employee-reviews/pending-reviews";
const overviewRoute = "/employee-reviews/overview";
const badRequestCode = 400;
const noAccessCode = 403;
const defaultGating = { canProceed: true, cantProceedTooltip: null };

// Trigger rows carry ids and dates of the page's own, so they are compared as they would be
// sent - a trigger removed and added back unchanged is not a change.
function toDirtyCheckedAnswers(answers, reviewOptions) {
    return { ...answers, employeeTriggers: answers.employeeTriggers.map((trigger) => toTriggerRequest(trigger, reviewOptions)) };
}

// The review's own entry for a topic, for topics whose items arrive with the review rather than
// from an endpoint of their own.
function findTopicEntry(review, topicKey) {
    return (review?.topics ?? []).find((topic) => topic.topicKey === topicKey) ?? null;
}

// Every answer the manager gives on the fixed steps is held in a single object so it can be
// dirty-checked against this default - otherwise work entered on a later step is silently lost
// when navigating away. Each topic step's own answers live in that topic's state.
const defaultWizardAnswers = {
    outcomeLevelNumber: null,
    outcomeSummary: "",
    customNextReviewDate: null,
    employeeSignOffMethodId: null,
    employeeSignature: null,
    signOffEmailAddress: "",
    managerSignature: null,
    confirmChecked: false,
    employeeTriggers: []
};

// The complete-review wizard (PBI 167757): a manager starts from an Introduction to the review and
// the employee's previous one, works through the review's topics, then Outcome, the driver's
// additional triggers where the review offers them, and Sign-off, to complete a pending review.
// Which topic steps the wizard shows is driven by the pending review's own topics and the topic
// modules in ./topics.
export function EmployeeReviewComplete(props) {

    // The wizard is reached either by a pending review's own id or, for a review raised for a
    // driver, by the employee and review type it is raised against. Both reads answer with the
    // same review, so which key the route carries only decides which endpoint is called.
    const { pendingReviewId, employeeId: routeEmployeeId, reviewTypeId } = useParams();
    const isAdHocReview = pendingReviewId == null;
    const navigate = useNavigate();
    const canPerformReview = UserHasAccess(requiredAction);

    const [loading, setLoading] = useState(true);
    const [invalidReview, setInvalidReview] = useState(false);
    const [pendingReview, setPendingReview] = useState(null);
    const [reviewOptions, setReviewOptions] = useState({});

    const [topicStates, setTopicStates] = useState(initialTopicStates);
    const [wizardAnswers, setWizardAnswers] = useState(defaultWizardAnswers);
    // The driver's additional triggers as the review loaded them, which the step starts from.
    const [loadedEmployeeTriggers, setLoadedEmployeeTriggers] = useState(defaultWizardAnswers.employeeTriggers);
    // Display-only: derived from the signatures and rendered as "Signed <date>" beneath each
    // one on the Sign-off step. Neither is sent to the API, so neither is dirty-tracked.
    const [managerSignedAt, setManagerSignedAt] = useState(null);
    const [employeeSignedAt, setEmployeeSignedAt] = useState(null);

    const [wizardStep, setWizardStep] = useState(0);
    const [saving, setSaving] = useState(false);
    // The employee's previous completed review of this type, read on its own so that a review which
    // cannot be read leaves the rest of the wizard unaffected.
    const [previousReview, setPreviousReview] = useState(null);
    const [previousReviewFailed, setPreviousReviewFailed] = useState(false);
    // Held here rather than in the Introduction step so a dismissed notice stays dismissed when the
    // manager steps back to the Introduction.
    const [triggerNoticeDismissed, setTriggerNoticeDismissed] = useState(false);
    const [, signOffEmailErrors, validateSignOffEmail] = useValidation();
    // The step the topic refresh below last acted on, starting at the step the wizard opens on.
    const refreshedWizardStep = useRef(0);

    const reviewEmployeeId = pendingReview?.employee?.id;

    // A route change from one review to another must reopen the wizard on its first step, so the
    // key identifies the review whichever way the route carries it - the pending review's own id,
    // or the employee and review type the review is raised against.
    let reviewKey = pendingReviewId;

    if (isAdHocReview) {
        reviewKey = `${routeEmployeeId}-${reviewTypeId}`;
    }

    // An employee and review type the route names may be inapplicable to that driver, unknown, or
    // another account's, all of which are answered alike and so cannot be told apart here. The
    // ad-hoc exit leads to the overview because that route is opened from more than one place and
    // the page cannot tell which.
    let exitText = "Back to Pending Reviews";
    let exitRoute = listingRoute;
    let exitId = "listing-pending-reviews";

    if (isAdHocReview) {
        exitText = "Back to Employee Reviews";
        exitRoute = overviewRoute;
        exitId = "listing-employee-reviews";
    }

    const topics = getReviewTopics(pendingReview);
    const topicsDirty = topics.some((topic) => topic.isDirty(topicStates[topic.key]));

    // The driver's own address, which the Sign-off step's email field opens with. Prefilling it is
    // not a change the manager has made, so it forms part of the clean state below - otherwise
    // simply opening the wizard would count as unsaved work.
    const signOffEmailPrefill = pendingReview?.employee?.emailAddress ?? "";

    // What the page looks like with nothing entered anywhere, for the single dirty check below. The
    // driver's triggers start as loaded, which is likewise not a change the manager has made.
    const cleanWizardAnswers = useMemo(() => {
        return { ...defaultWizardAnswers, signOffEmailAddress: signOffEmailPrefill, employeeTriggers: loadedEmployeeTriggers };
    }, [signOffEmailPrefill, loadedEmployeeTriggers]);

    const cleanWizardState = useMemo(() => {
        return { topicsDirty: false, answers: toDirtyCheckedAnswers(cleanWizardAnswers, reviewOptions) };
    }, [cleanWizardAnswers, reviewOptions]);

    // One dirty check covering every topic and the fixed steps' answers together, so the whole
    // wizard is dirty-tracked through a single useDirtyData instance.
    const wizardState = useMemo(() => {
        return { topicsDirty, answers: toDirtyCheckedAnswers(wizardAnswers, reviewOptions) };
    }, [topicsDirty, wizardAnswers, reviewOptions]);
    const isDirty = useDirtyData(cleanWizardState, wizardState);

    function updateTopic(topicKey, changes) {
        setTopicStates(function (previous) {
            const topicState = previous[topicKey];
            const resolvedChanges = typeof changes === "function" ? changes(topicState) : changes;

            return { ...previous, [topicKey]: { ...topicState, ...resolvedChanges } };
        });
    }

    function updateAnswer(field, value) {
        setWizardAnswers(function (previous) {
            return { ...previous, [field]: value };
        });
    }

    // Each topic loads on its own promise and handles its own failure, so one topic's record
    // being unavailable can neither prevent another's from loading nor stop the rest of the
    // wizard. The topic reports the gap on its own step and blocks completion from there.
    // isActive reports whether the read is still the page's own - a read for a review the route
    // has since moved off must not write its record into the review now on screen.
    function loadTopic(topic, topicEmployeeId, topicEntry, isActive) {
        return topic.load(topicEmployeeId, topicEntry).then(function (response) {
            if (!isActive()) {
                return;
            }

            updateTopic(topic.key, topic.applyLoaded(response));
        }, function () {
            if (!isActive()) {
                return;
            }

            updateTopic(topic.key, { loadFailed: true });
            NotificationToaster.show(Intent.DANGER, topic.loadFailureMessage);
        });
    }

    // A topic's record can also be written by another topic's own endpoint, so the topic is
    // re-read whenever its step becomes the active one. The read happens behind the values
    // already on screen - they are replaced when the response arrives, and kept as they are if
    // it fails, so moving between steps neither blanks a step nor gates it mid-navigation.
    function refreshTopic(topic, topicEmployeeId, topicEntry) {
        return topic.load(topicEmployeeId, topicEntry).then(function (response) {
            updateTopic(topic.key, function (topicState) {
                // The manager may have started editing, or a save may have begun, while the read
                // was in flight - their own values take precedence over the re-read record.
                if (topicState.editing === true || topicState.saving === true) {
                    return {};
                }

                return { ...topic.applyLoaded(response), loadFailed: false, refreshFailed: false };
            });
        }, function () {
            // The topic's save writes the whole record it holds back, so a record that could not be
            // checked for changes is held read-only - saving it could revert a change made since.
            updateTopic(topic.key, { refreshFailed: true });
            NotificationToaster.show(Intent.DANGER, topic.loadFailureMessage);
        });
    }

    useEffect(() => {
        // A read for the review the route has moved off can still be in flight, and resolving
        // out of order would leave the page showing that review's data under the replacement
        // review's id. Every response is therefore ignored once the read is no longer the
        // page's own.
        let active = true;

        function isActive() {
            return active;
        }

        function cleanup() {
            active = false;
        }

        if (!canPerformReview) {
            return cleanup;
        }

        // The route can move between reviews without the page remounting, so the state the
        // previous review left behind is cleared before its replacement is read.
        setLoading(true);
        setInvalidReview(false);
        setPendingReview(null);
        setTopicStates(initialTopicStates);
        setWizardAnswers(defaultWizardAnswers);
        setLoadedEmployeeTriggers(defaultWizardAnswers.employeeTriggers);
        setManagerSignedAt(null);
        setEmployeeSignedAt(null);
        setPreviousReview(null);
        setPreviousReviewFailed(false);
        setTriggerNoticeDismissed(false);
        setSaving(false);
        setWizardStep(0);
        refreshedWizardStep.current = 0;

        function errorHandler(error) {
            if (!active) {
                return;
            }

            const status = error?.response?.status ?? error?.status;

            if (status === badRequestCode || status === noAccessCode) {
                setInvalidReview(true);
            } else {
                NotificationToaster.show(Intent.DANGER, "Failed to get the pending review. Please refresh the page.");
            }
        }

        function loadPreviousReview(previousReviewId) {
            if (previousReviewId == null) {
                return;
            }

            EmployeeReviewsService.getCompletedReview(previousReviewId).then(function (response) {
                if (!active) {
                    return;
                }

                setPreviousReview(response);
            }, function () {
                if (!active) {
                    return;
                }

                setPreviousReviewFailed(true);
            });
        }

        function readReview() {
            if (isAdHocReview) {
                return EmployeeReviewsService.getReviewForCompletion(routeEmployeeId, reviewTypeId);
            }

            return EmployeeReviewsService.getPendingReview(pendingReviewId);
        }

        axios.all([
            readReview(),
            EmployeeReviewsService.getOptions()
        ]).then(axios.spread(function (pendingReviewResponse, optionsResponse) {
            if (!active) {
                return null;
            }

            setPendingReview(pendingReviewResponse);
            setReviewOptions(optionsResponse);
            loadPreviousReview(pendingReviewResponse.previousReviewId);
            // The address the sign-off email would go to is offered as it stands on the driver's
            // record, for the manager to correct for this review if it is wrong. It is seeded from
            // the review as it loaded and is theirs to edit from there.
            updateAnswer("signOffEmailAddress", pendingReviewResponse.employee?.emailAddress ?? "");

            const employeeTriggers = mapTriggersFromApi(pendingReviewResponse.triggerSettings?.employee, optionsResponse);
            setLoadedEmployeeTriggers(employeeTriggers);
            updateAnswer("employeeTriggers", employeeTriggers);

            // Only the topics the review covers are read, so a topic's data is never fetched for
            // a review that does not review it.
            return Promise.all(getReviewTopics(pendingReviewResponse).map(function (topic) {
                return loadTopic(topic, pendingReviewResponse.employee.id, findTopicEntry(pendingReviewResponse, topic.key), isActive);
            }));
        }), errorHandler).catch(errorHandler).finally(function () {
            if (!active) {
                return;
            }

            setLoading(false);
        });

        return cleanup;
    }, [pendingReviewId, routeEmployeeId, reviewTypeId, canPerformReview]);

    const signOffMethodKey = resolveEmployeeSignOffMethodKey(pendingReview, wizardAnswers.employeeSignOffMethodId);

    // The address is only asked for when the review is to be signed off by email, so choosing
    // another method leaves no message standing against a field the step no longer shows. It is
    // judged as it is sent - trimmed - so an address is not refused for whitespace that is dropped.
    useEffect(() => {
        let rules = [];

        if (signOffMethodKey === emailSignOffMethodKey) {
            rules = [{ fieldName: "signOffEmailAddress", required: true, type: "email" }];
        }

        validateSignOffEmail(rules, { signOffEmailAddress: (wizardAnswers.signOffEmailAddress ?? "").trim() });
    }, [signOffMethodKey, wizardAnswers.signOffEmailAddress]);

    function onWizardStepChange(step) {
        setWizardStep(step);
    }

    function onManagerSignatureChange(value) {
        const signature = toSignatureValue(value);
        updateAnswer("managerSignature", signature);
        setManagerSignedAt(signature == null ? null : new Date());
    }

    function onEmployeeSignatureChange(value) {
        const signature = toSignatureValue(value);
        updateAnswer("employeeSignature", signature);
        setEmployeeSignedAt(signature == null ? null : new Date());
    }

    // The employee's canvas is only offered for on-screen sign-off and is taken off the step with
    // any other method, so the signature it captured goes with it - a signature drawn on screen
    // cannot stand against a method that is not signed on screen.
    function onSignOffMethodChange(value) {
        setWizardAnswers(function (previous) {
            return { ...previous, employeeSignOffMethodId: value, employeeSignature: null };
        });
        setEmployeeSignedAt(null);
    }

    function onFinish() {
        const request = buildCompleteReviewRequest({
            pendingReview,
            topicComments: buildTopicComments(topics, topicStates),
            topicRequestFields: buildTopicRequestFields(topics, topicStates),
            ...wizardAnswers,
            reviewOptions
        });

        setSaving(true);

        function submitReview() {
            if (isAdHocReview) {
                return EmployeeReviewsService.completeAdHocReview(routeEmployeeId, reviewTypeId, request);
            }

            return EmployeeReviewsService.completeReview(pendingReviewId, request);
        }

        submitReview().then(function (response) {
            NotificationToaster.show(Intent.SUCCESS, "Review has been successfully completed.");

            // Everything entered is now saved, so it is cleared back to the default - otherwise
            // the unsaved-changes alert would block the wizard's own success redirect. Each
            // topic's record was persisted through its own endpoint before this point, or sent
            // with the completion request itself.
            setTopicStates(initialTopicStates());
            setWizardAnswers(cleanWizardAnswers);
            // Redirect at the end of the render cycle, once the alert has seen the reset.
            setTimeout(() => {
                navigate(`/employee-reviews/completed-reviews/${response.reviewId}`);
            }, 100);
        }, function (error) {
            if (error?.status === badRequestCode) {
                NotificationToaster.show(Intent.WARNING, error.data);
            } else {
                NotificationToaster.show(Intent.DANGER, "Failed to complete the review. Please try again.");
            }
        }).finally(function () {
            setSaving(false);
        });
    }

    const statusBand = statusBands[pendingReview?.statusBand];
    const employeeName = pendingReview?.employee?.name;

    // The banner says what completing this review does to the driver's pending review of the type.
    // On a review type without a schedule, completing moves it to the next review date chosen on
    // the Outcome step, or removes it when no date is set or the type allows none. On a scheduled
    // type, satisfiesScheduledReview says whether the completion moves the schedule on or leaves
    // the scheduled review outstanding - not whether the review is due. A scheduleActive missing
    // from the review is read as scheduled, and a driver with no pending review of the type is
    // told nothing.
    const scheduledReviewId = pendingReview?.pendingReviewId;
    const dueDate = <DateToLocal format="DD/MM/YYYY">{pendingReview?.dueDate}</DateToLocal>;
    let scheduleImpactText = null;
    let scheduleImpactIntent = null;

    if (scheduledReviewId != null && pendingReview?.scheduleActive === false) {
        if (pendingReview.allowCustomNextReviewDate === true) {
            scheduleImpactText = (<>{employeeName}'s next review is due {dueDate}. Completing this review moves it to the date you choose on the Outcome step, or removes it if you leave the date blank.</>);
        } else {
            scheduleImpactText = (<>{employeeName}'s next review is due {dueDate}. Completing this review removes it.</>);
        }
        scheduleImpactIntent = "primary";
    } else if (scheduledReviewId != null && pendingReview?.satisfiesScheduledReview === true) {
        const scheduledNextReviewDate = pendingReview.scheduledNextReviewDate;
        const nextReviewDate = <DateToLocal format="DD/MM/YYYY">{scheduledNextReviewDate}</DateToLocal>;

        // Dates are compared on the local calendar day DateToLocal displays. When the scheduled
        // review is already due on the date this completion sets, quoting both would read as the
        // same review being completed twice, so only the next date is given.
        if (scheduledNextReviewDate == null) {
            scheduleImpactText = (<>This counts as the scheduled review due {dueDate}.</>);
        } else if (moment.utc(pendingReview.dueDate).local().isSame(moment.utc(scheduledNextReviewDate).local(), 'day')) {
            scheduleImpactText = (<>This counts as the scheduled review. The next review will be due {nextReviewDate}.</>);
        } else {
            scheduleImpactText = (<>This counts as the scheduled review due {dueDate}. The next review will be due {nextReviewDate}.</>);
        }
        scheduleImpactIntent = "primary";
    } else if (scheduledReviewId != null) {
        scheduleImpactText = (<>This is an extra review. {employeeName}'s scheduled review is still due {dueDate} and will remain outstanding.</>);
        scheduleImpactIntent = "warning";
    }

    // A review due on the schedule is headed with its due date. Any other is headed with what raised
    // it, as the date the API gives a review raised manually or by a trigger is not one it is due on.
    // While the review is loading the due date's skeleton stands in for either.
    const raisedBySchedule = loading || pendingReview?.raisedBy === "Schedule";

    const outcomeLevelValidationMessage =
        pendingReview?.outcomeLevels?.active && pendingReview?.outcomeLevels?.mandatory && wizardAnswers.outcomeLevelNumber == null
            ? "An outcome level is required for this review type."
            : null;

    // A review carries trigger settings only when one of its topics can have triggers, and only
    // then does the wizard offer the driver's triggers. The review type's own are fixed here, and
    // every driver trigger is checked against them as well as against the driver's others.
    const hasTriggerSettings = pendingReview?.triggerSettings != null;

    const reviewTypeTriggers = useMemo(() => {
        return mapTriggersFromApi(pendingReview?.triggerSettings?.reviewType, reviewOptions);
    }, [pendingReview, reviewOptions]);

    const triggerTopicIds = useMemo(() => {
        return getTopicIdsByKeys((pendingReview?.topics ?? []).map((topic) => topic.topicKey), reviewOptions);
    }, [pendingReview, reviewOptions]);

    const triggerValidationRules = useMemo(() => {
        if (!hasTriggerSettings) {
            return [];
        }

        return getTriggerValidationRules(wizardAnswers.employeeTriggers, reviewOptions, { coveringTriggers: reviewTypeTriggers });
    }, [hasTriggerSettings, wizardAnswers.employeeTriggers, reviewOptions, reviewTypeTriggers]);

    function renderTopicStep(topic) {
        const TopicStep = topic.Step;

        return (
            <TopicStep
                key={topic.key}
                state={topicStates[topic.key]}
                employeeId={reviewEmployeeId}
                loading={loading}
                onChange={(changes) => updateTopic(topic.key, changes)}
            />
        );
    }

    // The Introduction, one step per topic the review covers, then Outcome, the driver's additional
    // triggers when the review offers them, and Sign-off, which closes every review whichever topics
    // it covers. The Wizard takes its step titles and its children separately, and both are built
    // from this one list so they cannot disagree.
    const wizardSteps = [
        {
            stepTitle: "Introduction",
            gating: defaultGating,
            content: (
                <IntroductionStep
                    key="INTRODUCTION"
                    topicTitles={topics.map((topic) => topic.stepTitle)}
                    raisedBy={pendingReview?.raisedBy}
                    triggers={pendingReview?.triggers}
                    triggerNoticeDismissed={triggerNoticeDismissed}
                    onTriggerNoticeDismiss={() => setTriggerNoticeDismissed(true)}
                    previousReviewId={pendingReview?.previousReviewId}
                    previousReview={previousReview}
                    previousReviewFailed={previousReviewFailed}
                    loading={loading}
                />
            )
        },
        ...topics.map((topic) => ({
            stepTitle: topic.stepTitle,
            topic,
            gating: topic.getGating(topicStates[topic.key]),
            content: renderTopicStep(topic)
        })),
        {
            stepTitle: "Outcome",
            gating: buildOutcomeGating(pendingReview, wizardAnswers),
            content: (
                <OutcomeStep
                    key="OUTCOME"
                    pendingReview={pendingReview}
                    reviewOptions={reviewOptions}
                    outcomeLevelNumber={wizardAnswers.outcomeLevelNumber}
                    outcomeSummary={wizardAnswers.outcomeSummary}
                    customNextReviewDate={wizardAnswers.customNextReviewDate}
                    loading={loading}
                    saving={saving}
                    outcomeLevelValidationMessage={outcomeLevelValidationMessage}
                    previousReview={previousReview}
                    onOutcomeLevelChange={(value) => updateAnswer("outcomeLevelNumber", value)}
                    onOutcomeSummaryChange={(event) => updateAnswer("outcomeSummary", event.target.value)}
                    onCustomNextReviewDateChange={(value) => updateAnswer("customNextReviewDate", value)}
                />
            )
        },
        ...(hasTriggerSettings ? [{
            stepTitle: "Additional triggers",
            gating: buildAdditionalTriggersGating(triggerValidationRules),
            content: (
                <AdditionalTriggersStep
                    key="ADDITIONALTRIGGERS"
                    reviewTypeTriggers={reviewTypeTriggers}
                    employeeTriggers={wizardAnswers.employeeTriggers}
                    topicIds={triggerTopicIds}
                    reviewOptions={reviewOptions}
                    errors={toTriggerErrors(triggerValidationRules)}
                    loading={loading}
                    saving={saving}
                    onEmployeeTriggersChange={(value) => updateAnswer("employeeTriggers", value)}
                />
            )
        }] : []),
        {
            stepTitle: "Sign-off",
            gating: buildSignOffGating(pendingReview, wizardAnswers),
            content: (
                <SignOffStep
                    key="SIGNOFF"
                    signOff={pendingReview?.signOff}
                    employeeSignOffMethodId={wizardAnswers.employeeSignOffMethodId}
                    employeeSignature={wizardAnswers.employeeSignature}
                    employeeSignedAt={employeeSignedAt}
                    signOffEmailAddress={wizardAnswers.signOffEmailAddress}
                    signOffEmailValidationMessage={signOffEmailErrors.signOffEmailAddress}
                    managerSignature={wizardAnswers.managerSignature}
                    managerSignedAt={managerSignedAt}
                    confirmChecked={wizardAnswers.confirmChecked}
                    loading={loading}
                    saving={saving}
                    onSignOffMethodChange={onSignOffMethodChange}
                    onEmployeeSignatureChange={onEmployeeSignatureChange}
                    onSignOffEmailAddressChange={(value) => updateAnswer("signOffEmailAddress", value)}
                    onManagerSignatureChange={onManagerSignatureChange}
                    onConfirmChange={(value) => updateAnswer("confirmChecked", value)}
                />
            )
        }
    ];

    // Moving to a topic step re-reads that topic, so the step presents - and its save sends - the
    // record as it now stands rather than as it stood when the wizard opened. Only a genuine move
    // to a different step qualifies, so the initial load is never repeated. The topic is taken from
    // the step itself, as only a topic's own step carries one.
    useEffect(() => {
        const previousStep = refreshedWizardStep.current;
        refreshedWizardStep.current = wizardStep;

        if (loading || wizardStep === previousStep) {
            return;
        }

        const topic = wizardSteps[wizardStep]?.topic;

        if (topic == null) {
            return;
        }

        // A topic being edited or saved keeps what the manager has entered.
        const topicState = topicStates[topic.key];

        if (topicState?.editing === true || topicState?.saving === true) {
            return;
        }

        refreshTopic(topic, reviewEmployeeId, findTopicEntry(pendingReview, topic.key));
    }, [wizardStep, loading]);

    // The Wizard applies canProceed/cantProceedTooltip to whichever button (Next or Finish) is
    // visible for the active step, so gating is taken from the active step's own rule rather than
    // from its position in the wizard.
    const activeStepGating = wizardSteps[wizardStep]?.gating ?? defaultGating;
    const canProceed = !loading && !saving && activeStepGating.canProceed;

    return (
        <UserAccess perform={requiredAction}
            yes={() => (
                <ShowHide
                    evaluator={invalidReview}
                    show={(
                        <div className="row">
                            <Breadcrumbs items={props.breadcrumbs} />
                            <NotificationInline
                                allowClose={false}
                                show
                                text="This review does not exist, or you do not have permission to complete it"
                                intent="danger"
                            />
                            <div>
                                <LinkButton intent="primary" text={exitText} id={exitId} href={exitRoute} />
                            </div>
                        </div>
                    )}
                    hide={(
                        <Fragment>
                            <div className="row">
                                <Breadcrumbs items={props.breadcrumbs} />
                                <h1 className={classNames({ "bp6-skeleton": loading })}>Complete Review</h1>
                                <GridLayout numberOfColumns={4} className="spacer-bottom-medium">
                                    <DataDisplayRecord
                                        loading={loading}
                                        header="Employee"
                                        data={(
                                            // Opens the employee's own Reviews tab in a new tab so the
                                            // manager keeps their place in the wizard. Falls back to plain
                                            // text while loading, or if the payload carries no employee id.
                                            <ShowHide
                                                evaluator={!loading && reviewEmployeeId != null}
                                                show={(
                                                    <Link to={`/employee/${reviewEmployeeId}/reviews`} target="_blank" rel="noopener noreferrer">{employeeName}</Link>
                                                )}
                                                hide={employeeName}
                                            />
                                        )}
                                    />
                                    <DataDisplayRecord loading={loading} header="Review Type" data={pendingReview?.reviewType?.name} />
                                    <ShowHide
                                        evaluator={raisedBySchedule}
                                        show={(
                                            <DataDisplayRecord loading={loading} header="Due Date" data={<DateToLocal format="DD/MM/YYYY">{pendingReview?.dueDate}</DateToLocal>} />
                                        )}
                                        hide={(
                                            <DataDisplayRecord loading={loading} header="Raised By" data={<RaisedByDisplay raisedBy={pendingReview?.raisedBy} triggers={pendingReview?.triggers} />} />
                                        )}
                                    />
                                    <DataDisplayRecord loading={loading} header="Status" data={statusBand != null ? <span className={statusBand.className}>{statusBand.label}</span> : null} />
                                </GridLayout>
                                <ShowHide
                                    evaluator={isAdHocReview && scheduleImpactText != null}
                                    show={(
                                        <NotificationInline
                                            allowClose={false}
                                            show
                                            text={scheduleImpactText}
                                            intent={scheduleImpactIntent}
                                        />
                                    )}
                                />
                            </div>
                            <div className="row">
                                <ShowHide
                                    evaluator={loading}
                                    show={(
                                        // Which steps the wizard has is the review's own topic set, so it cannot be
                                        // built until the pending review has arrived. Until then the step strip
                                        // stands in for it in its own loading state.
                                        <div className="wizard">
                                            <WizardSteps
                                                currentStep={0}
                                                totalSteps={0}
                                                loading
                                                minimal
                                                showStepTitles
                                                onStepClick={() => null}
                                            />
                                        </div>
                                    )}
                                    hide={(
                                        <Wizard
                                            // The wizard tracks its own position, so a different review gets its
                                            // own instance and opens on the first step rather than inheriting the
                                            // step the previous review was left on.
                                            key={reviewKey}
                                            onStepChange={onWizardStepChange}
                                            onFinish={onFinish}
                                            canProceed={canProceed}
                                            cantProceedTooltip={activeStepGating.cantProceedTooltip}
                                            additionalStepInformation={wizardSteps.map((step) => ({ stepTitle: step.stepTitle }))}
                                            showStepTitles
                                            // The wizard counts its own steps a commit after mounting, so completion is
                                            // offered against the step set here rather than against that count.
                                            canFinish={wizardStep === wizardSteps.length - 1}
                                            disabled={saving}
                                            loading={loading}
                                            alignButtonsRight
                                            finishButtonText="COMPLETE REVIEW"
                                        >
                                            {wizardSteps.map((step) => step.content)}
                                        </Wizard>
                                    )}
                                />
                            </div>
                            <AlertUnsavedData isDirty={isDirty} />
                        </Fragment>
                    )}
                />
            )}
            no={() => (
                <PageRestricted />
            )}
        />
    );
}

EmployeeReviewComplete.propTypes = {
    breadcrumbs: PropTypes.array
};
